import { datadogRum } from '@datadog/browser-rum'

import { CONFIG_DEBUG_EVENT_LOGGING, DD_RUM_ENABLED_FLAG } from '@src/config/env'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getStorage } from '@src/utils/enhancedStorage'

import { IExperimentLoggingData } from '../experiments.types'

import { StatsigExperimentBucket } from './statsig.types'

const LOCALSTORAGE_KEY = 'statsigExperimentBuckets'
const DATADOG_STATSIG_EXPOSED_BUCKETS_CONTEXT_KEY = 'statsigExposedBuckets'
const DATADOG_STATSIG_EXPOSED_BUCKETS_MAX_LENGTH = 300

function truncateStatsigExposedBuckets(serializedActiveTreatmentBuckets: string): string {
  if (serializedActiveTreatmentBuckets.length <= DATADOG_STATSIG_EXPOSED_BUCKETS_MAX_LENGTH) {
    return serializedActiveTreatmentBuckets
  }

  const lastBoundary = serializedActiveTreatmentBuckets.lastIndexOf('__', DATADOG_STATSIG_EXPOSED_BUCKETS_MAX_LENGTH)
  const end = lastBoundary === -1 ? DATADOG_STATSIG_EXPOSED_BUCKETS_MAX_LENGTH : lastBoundary
  return serializedActiveTreatmentBuckets.slice(0, end)
}

export class StatsigExperimentManager {
  exposedBuckets: Record<string, StatsigExperimentBucket>

  constructor() {
    this.exposedBuckets = {}
  }

  initialize() {
    const existingBuckets = this.getExposedBucketsFromLocalStorage()

    existingBuckets.forEach(
      ({ experimentName, bucketName, isActive } = { experimentName: '', bucketName: '', isActive: false }) => {
        if (!experimentName || !bucketName) return
        this.exposedBuckets[experimentName] = {
          experimentName,
          bucketName,
          isActive,
        }
      },
    )
    this.syncStatsigExposedBucketsWithDatadog()
  }

  setExposedBuckets(buckets: StatsigExperimentBucket[], replaceBuckets = false) {
    if (replaceBuckets) {
      this.exposedBuckets = {}
    }
    buckets.forEach(bucket => {
      this.exposedBuckets[bucket.experimentName] = bucket
    })

    this.setExposedBucketsInLocalStorage()
    this.syncStatsigExposedBucketsWithDatadog()
  }

  getExposedBuckets() {
    const experimentBuckets: StatsigExperimentBucket[] = []
    Object.entries(this.exposedBuckets).forEach(([experimentName, bucket]) => {
      experimentBuckets.push({
        experimentName,
        bucketName: bucket.bucketName,
        isActive: bucket.isActive,
      })
    })
    return Object.freeze(experimentBuckets)
  }

  getActiveExposedBuckets() {
    return this.getExposedBuckets().filter(bucket => {
      // bucket is active can be undefined for users not exposed to experiment after isActive key was added
      return bucket.isActive ?? true
    })
  }

  private setExposedBucketsInLocalStorage() {
    try {
      if (!isBrowser()) return void 0

      const experimentBuckets = this.getExposedBuckets()

      getStorage().setItem(LOCALSTORAGE_KEY, JSON.stringify(experimentBuckets))
    } catch (error) {
      console.error(`Failed to set Statsig Experiment buckets in Local storage: `, error)
    }
  }

  private getExposedBucketsFromLocalStorage() {
    try {
      if (!isBrowser()) return []

      const existingBuckets = JSON.parse(getStorage().getItem(LOCALSTORAGE_KEY) ?? '[]') as IExperimentLoggingData[]
      if (!Array.isArray(existingBuckets)) {
        return []
      }
      return existingBuckets
    } catch (error) {
      console.error(`Failed to get Statsig Experiment buckets from Local storage: `, error)
      return []
    }
  }

  private syncStatsigExposedBucketsWithDatadog() {
    if (!isBrowser() || !DD_RUM_ENABLED_FLAG) return

    try {
      const activeTreatmentBuckets = this.getActiveExposedBuckets().filter(
        bucket => bucket.bucketName.toLowerCase() !== 'control',
      )

      const serializedActiveTreatmentBuckets = activeTreatmentBuckets
        .sort((a, b) => a.experimentName.localeCompare(b.experimentName))
        .map(bucket => `${bucket.experimentName}_${bucket.bucketName}`)
        .join('__')

      const statsigExposedBuckets = truncateStatsigExposedBuckets(serializedActiveTreatmentBuckets)

      datadogRum.setGlobalContextProperty(DATADOG_STATSIG_EXPOSED_BUCKETS_CONTEXT_KEY, statsigExposedBuckets)

      if (CONFIG_DEBUG_EVENT_LOGGING) {
        console.log('Statsig exposed buckets injected into Datadog RUM', {
          contextKey: DATADOG_STATSIG_EXPOSED_BUCKETS_CONTEXT_KEY,
          statsigExposedBuckets,
          activeTreatmentBuckets,
        })
      }
    } catch (error) {
      console.error(`Failed to sync Statsig Experiment buckets with Datadog: `, error)
    }
  }
}

export const statsigExperimentManager = new StatsigExperimentManager()
